// src/pages/msp-inventory/pages/reports/PnLReportPage.jsx
import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ChevronLeft, AlertTriangle, List } from "lucide-react";
import DateRangePicker, { rangeToParams } from "../../components/reports/DateRangePicker";
import KpiCard from "../../components/reports/KpiCard";
import { SortableTH, Pagination } from "../../components/reports/TableControls";
import { getPnlReport } from "../../services/catalogService";
import { usePersistedState } from "../../hooks/usePersistedState";
import { useSortAndPaginate } from "../../hooks/useSortAndPaginate";
import RefreshableSection from "../../components/reports/RefreshableSection";
import { buildCacheKey, getCachedReport, setCachedReport, invalidateCachedReport } from "../../utils/reportCache";
import RefreshReportBar from "../../components/reports/RefreshReportBar";
import ExportCsvButton from "../../components/reports/ExportCsvButton";
import { downloadCsv } from "../../utils/exportCsv";
import LoadingGame from "../../components/reports/LoadingGame";
import LineItemsDrawer, { ClickableTotal } from "../../components/reports/LineItemsDrawer";

const PnLReportPage = ({ onOpenSku }) => {
    const [range, setRange] = usePersistedState("reports:pnl:range", { key: "6m" });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    // When set (via clicking a bar), drills the chart down into that
    // month's daily line view — independent of the 7d/30d "always daily"
    // behavior below, and cleared whenever the range picker changes.
    const [drilldownMonth, setDrilldownMonth] = useState(null);
    // "What makes up this number" drawer: null = closed.
    const [breakdown, setBreakdown] = useState(null);
    const openBreakdown = (opts) => setBreakdown({ defaultView: "lines", defaultMissingOnly: false, ...opts });
    const rangeLabel = range.key === "custom" ? `${range.start} → ${range.end}` : `Range: ${range.key.toUpperCase()}`;

    const cacheKey = buildCacheKey("pnl", rangeToParams(range));

    const loadReport = async (forceRefresh = false) => {
        const isInitialLoad = !data;

        if (!forceRefresh) {
            const cached = await getCachedReport(cacheKey);
            if (cached) {
                setData(cached.data);
                setSavedAt(cached.savedAt);
                setLoading(false);
                return;
            }
        }

        if (isInitialLoad) setLoading(true);
        else setRefreshing(true);
        try {
            const d = await getPnlReport(rangeToParams(range));
            setData(d);
            setSavedAt(Date.now());
            setCachedReport(cacheKey, d);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        loadReport();
        setDrilldownMonth(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range]);

    const handleForceRefresh = async () => {
        await invalidateCachedReport(cacheKey);
        await loadReport(true);
    };

    // Mirrors the backend's resolve_range so we know exactly where the
    // selected window STARTS. If that start date isn't the 1st of its
    // month, that first bucket is a truncation artifact (e.g. a 90-day
    // window starting April 29 gives April only 2 days of data) and gets
    // dropped from the chart AND the table below it. The CURRENT month is
    // never dropped this way — even if today is July 15, July's partial
    // data is real progress, not an artifact of where the range started.
    const computeRangeStartDate = (r) => {
        const now = new Date();
        if (r.key === "custom") return r.start ? new Date(r.start) : null;
        if (r.key === "ytd") return new Date(now.getFullYear(), 0, 1);
        const daysMap = { "7d": 7, "30d": 30, "90d": 90, "6m": 182, "12m": 365 };
        const d = new Date(now);
        d.setDate(d.getDate() - (daysMap[r.key] || 30));
        return d;
    };
    const rangeStart = computeRangeStartDate(range);
    const startMonthKey = rangeStart ? rangeStart.toISOString().slice(0, 7) : null;
    const startIsPartial = rangeStart && rangeStart.getDate() !== 1;

    const monthsAscending = [...(data?.by_month || [])].sort((a, b) => a.month.localeCompare(b.month));
    // The set of months actually shown on the bar chart — the table below
    // is filtered to this exact same set, so the two never disagree.
    const completedMonths = monthsAscending.filter((m, idx) =>
        !(idx === 0 && startIsPartial && m.month === startMonthKey)
    );

    // Short windows (7d/30d) don't hold up as a monthly bar chart — a week
    // or a single month barely fills 1-2 bars. Show a daily line instead.
    const useDailyView = range.key === "7d" || range.key === "30d";
    const dailySeries = data?.by_day || [];
    // Clicking a month bar (only possible in the monthly bar view) filters
    // this same by_day series down to just that month — by_day already
    // covers the WHOLE selected range, so no extra fetch is needed.
    const drilldownSeries = drilldownMonth ? dailySeries.filter((d) => d.date.startsWith(drilldownMonth)) : [];

    const showingDaily = useDailyView || !!drilldownMonth;
    const chartData = drilldownMonth ? drilldownSeries : (useDailyView ? dailySeries : completedMonths);

    const { sorted, pageItems, page, setPage, totalPages, sortConfig, requestSort, pageSize } =
        useSortAndPaginate(completedMonths, { key: "month", dir: "desc" }, "reports:pnl:table");

    if (loading && !data) return <LoadingGame label="Loading P&L..." />;
    if (!data) return null;

    const { totals } = data;

    const handleBarClick = (entry) => {
        if (!entry || !entry.month) return;
        setDrilldownMonth(entry.month);
    };

    // Whole selected range, reconciled against one of the KPI totals.
    const drillRange = (label, field, expected, missingOnly = false) => openBreakdown({
        title: missingOnly ? "Order lines missing cost" : `${label} — all order lines`,
        subtitle: rangeLabel,
        params: rangeToParams(range),
        defaultMissingOnly: missingOnly,
        reconcile: field ? { label, field, expected } : undefined,
    });

    const openMonth = (m, missingOnly = false) => openBreakdown({
        title: `${m.month} — order lines`,
        subtitle: rangeLabel,
        params: { ...rangeToParams(range), month: m.month },
        defaultMissingOnly: missingOnly,
        reconcile: { label: "Revenue", field: "revenue", expected: m.revenue },
    });

    const handleDayClick = (entry) => {
        if (!entry || !entry.date) return;
        openBreakdown({
            title: `Orders on ${entry.date}`,
            subtitle: rangeLabel,
            params: { ...rangeToParams(range), date: entry.date },
            reconcile: { label: "Revenue", field: "revenue", expected: entry.revenue },
        });
    };

    return (
        <div className="max-w-6xl mx-auto py-8 px-6">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">P&L Report</h1>
                    <p className="text-gray-500 text-sm">Revenue, COGS, and margin over time</p>
                </div>
                <div className="flex items-center gap-2">
                    <RefreshReportBar savedAt={savedAt} refreshing={refreshing} onRefresh={handleForceRefresh} onAutoRefresh={handleForceRefresh} />
                    <button type="button" onClick={() => drillRange("Revenue", "revenue", totals.revenue)}
                        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
                        <List size={14} /> View Orders
                    </button>
                    <button type="button" onClick={() => drillRange("Revenue", null, null, true)}
                        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100">
                        <AlertTriangle size={14} /> Missing Costs{(totals.missing_cost_count || 0) > 0 ? ` (${totals.missing_cost_count})` : ""}
                    </button>
                    <ExportCsvButton
                        label="Download Fees Report"
                        onExport={() => downloadCsv(
                            `fees_report_${new Date().toISOString().slice(0, 10)}.csv`,
                            ["Month", "Referral Fee", "WFS Fee", "Fulfillment Cost", "Inbound Shipping", "Sales Tax", "Total Platform Fees"],
                            completedMonths.map((m) => [
                                m.month,
                                m.fee_breakdown_totals.referral_fee.toFixed(2),
                                m.fee_breakdown_totals.wfs_fee.toFixed(2),
                                m.fee_breakdown_totals.fulfillment_cost.toFixed(2),
                                m.fee_breakdown_totals.inbound_shipping.toFixed(2),
                                m.fee_breakdown_totals.sales_tax.toFixed(2),
                                m.platform_fees.toFixed(2),
                            ]),
                            [
                                ["Referral Fee", totals.fee_breakdown_totals.referral_fee.toFixed(2)],
                                ["WFS Fee", totals.fee_breakdown_totals.wfs_fee.toFixed(2)],
                                ["Fulfillment Cost", totals.fee_breakdown_totals.fulfillment_cost.toFixed(2)],
                                ["Inbound Shipping", totals.fee_breakdown_totals.inbound_shipping.toFixed(2)],
                                ["Sales Tax", totals.fee_breakdown_totals.sales_tax.toFixed(2)],
                                ["Total Platform Fees", totals.platform_fees.toFixed(2)],
                            ]
                        )}
                    />
                    <ExportCsvButton label="FULL Report" onExport={() => downloadCsv(
                        `pnl_report_${new Date().toISOString().slice(0, 10)}.csv`,
                        ["Month", "Revenue", "COGS", "Shipping", "Platform Fees", "Referral Fee", "WFS Fee",
                            "Fulfillment Cost", "Inbound Shipping", "Sales Tax", "Net Profit", "Net Margin %", "Units Sold"],
                        completedMonths.map((m) => [
                            m.month, m.revenue.toFixed(2), m.cogs.toFixed(2), m.shipping_cost.toFixed(2),
                            m.platform_fees.toFixed(2),
                            m.fee_breakdown_totals.referral_fee.toFixed(2), m.fee_breakdown_totals.wfs_fee.toFixed(2),
                            m.fee_breakdown_totals.fulfillment_cost.toFixed(2), m.fee_breakdown_totals.inbound_shipping.toFixed(2),
                            m.fee_breakdown_totals.sales_tax.toFixed(2),
                            m.net_profit.toFixed(2), m.net_margin_pct, m.units_sold,
                        ]),
                        [
                            ["Revenue", totals.revenue.toFixed(2)], ["COGS", totals.cogs.toFixed(2)],
                            ["Shipping Cost", totals.shipping_cost.toFixed(2)], ["Platform Fees", totals.platform_fees.toFixed(2)],
                            ["Referral Fee", totals.fee_breakdown_totals.referral_fee.toFixed(2)],
                            ["WFS Fee", totals.fee_breakdown_totals.wfs_fee.toFixed(2)],
                            ["Fulfillment Cost", totals.fee_breakdown_totals.fulfillment_cost.toFixed(2)],
                            ["Inbound Shipping", totals.fee_breakdown_totals.inbound_shipping.toFixed(2)],
                            ["Sales Tax", totals.fee_breakdown_totals.sales_tax.toFixed(2)],
                            ["Net Profit", totals.net_profit.toFixed(2)], ["Net Margin %", totals.net_margin_pct],
                            ["Units Sold", totals.units_sold],
                        ]
                    )} />
                    <DateRangePicker range={range} setRange={setRange} />
                </div>
            </div>

            {(totals.missing_cost_count || 0) > 0 && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-xs text-amber-800 flex items-center gap-2">
                        <AlertTriangle size={16} />
                        <span><b>{totals.missing_cost_count} order lines</b> ({totals.missing_cost_units ?? "?"} units, ${Number(totals.missing_cost_revenue || 0).toFixed(2)} revenue) have no cost. Net profit below is overstated until they're fixed.</span>
                    </p>
                    <button type="button" onClick={() => drillRange("Revenue", null, null, true)}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700">
                        See which ones
                    </button>
                </div>
            )}

            <RefreshableSection refreshing={refreshing} className="mb-6">
                {(() => {
                    // Uses the RANGE THE USER PICKED (e.g. "6m" -> 6), not a count of
                    // partial calendar-month buckets in by_month — a 6-month range
                    // touching 7 distinct calendar months at its edges shouldn't make
                    // the label say "7 months" when the user chose 6.
                    const RANGE_TO_MONTHS = { "7d": 0.25, "30d": 1, "90d": 3, "6m": 6, "12m": 12 };
                    const monthsLabel = range.key === "ytd"
                        ? `${new Date().getMonth() + 1} mo (YTD)`
                        : range.key === "custom"
                            ? null
                            : `${RANGE_TO_MONTHS[range.key] || 1} mo`;
                    const monthsForAvg = range.key === "ytd"
                        ? Math.max(1, new Date().getMonth() + 1)
                        : range.key === "custom"
                            ? Math.max(1, (data.by_month || []).length)
                            : (RANGE_TO_MONTHS[range.key] || 1);
                    const perMonth = (value) => monthsLabel
                        ? `avg $${(value / monthsForAvg).toFixed(2)}/mo (${monthsLabel})`
                        : `avg $${(value / monthsForAvg).toFixed(2)}/mo`;

                    return (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <ClickableTotal onClick={() => drillRange("Revenue", "revenue", totals.revenue)}>
                                <KpiCard label="Revenue" value={`$${totals.revenue.toFixed(2)}`} sublabel={perMonth(totals.revenue)} color="teal" />
                            </ClickableTotal>
                            <ClickableTotal onClick={() => drillRange("COGS", "cogs", totals.cogs)}>
                                <KpiCard label="COGS" value={`$${totals.cogs.toFixed(2)}`} sublabel={perMonth(totals.cogs)} color="orange" />
                            </ClickableTotal>
                            <div className="relative group">
                                <ClickableTotal onClick={() => drillRange("Platform Fees", "platform_fees", totals.platform_fees)}>
                                    <KpiCard label="Est. Platform Fees (hover)" value={`$${totals.platform_fees.toFixed(2)}`} sublabel={perMonth(totals.platform_fees)} color="orange" />
                                </ClickableTotal>
                                <div className="hidden group-hover:block absolute z-20 top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-64 text-left">
                                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-1.5">Fee Breakdown (this range)</p>
                                    <div className="space-y-1 text-xs">
                                        <div className="flex justify-between"><span className="text-gray-500">Referral Fee</span><span className="font-semibold text-gray-700">${totals.fee_breakdown_totals.referral_fee.toFixed(2)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-500">WFS Fee</span><span className="font-semibold text-gray-700">${totals.fee_breakdown_totals.wfs_fee.toFixed(2)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-500">Fulfillment Cost</span><span className="font-semibold text-gray-700">${totals.fee_breakdown_totals.fulfillment_cost.toFixed(2)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-500">Inbound Shipping</span><span className="font-semibold text-gray-700">${totals.fee_breakdown_totals.inbound_shipping.toFixed(2)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-500">Sales Tax (on cost)</span><span className="font-semibold text-gray-700">${totals.fee_breakdown_totals.sales_tax.toFixed(2)}</span></div>
                                        <div className="flex justify-between pt-1 border-t border-gray-100"><span className="text-gray-600 font-bold">Total</span><span className="font-bold text-red-600">${totals.platform_fees.toFixed(2)}</span></div>
                                    </div>
                                </div>
                            </div>
                            <ClickableTotal onClick={() => drillRange("Net Profit", "net_profit", totals.net_profit)}>
                                <KpiCard label="Est. Net Profit" value={`$${totals.net_profit.toFixed(2)}`}
                                    sublabel={`${totals.net_margin_pct}% net margin · ${perMonth(totals.net_profit)}`} color={totals.net_profit >= 0 ? "green" : "red"} />
                            </ClickableTotal>
                            <ClickableTotal onClick={() => drillRange("Units", "qty", totals.units_sold)}>
                                <KpiCard label="Units Sold" value={totals.units_sold} sublabel={`avg ${(totals.units_sold / monthsForAvg).toFixed(0)}/mo`} color="purple" />
                            </ClickableTotal>
                        </div>
                    );
                })()}
            </RefreshableSection>

            <RefreshableSection refreshing={refreshing} className="mb-6">
                <div className="bg-white rounded-2xl shadow-lg p-5">
                    <div className="flex items-center justify-between mb-1">
                        <h3 className="font-bold text-gray-800 text-sm flex items-center gap-2">
                            {drilldownMonth && (
                                <button onClick={() => setDrilldownMonth(null)}
                                    className="text-gray-400 hover:text-gray-600 flex items-center gap-1 text-xs font-semibold">
                                    <ChevronLeft size={14} /> Back
                                </button>
                            )}
                            {drilldownMonth
                                ? `Daily Revenue vs COGS — ${drilldownMonth}`
                                : showingDaily ? "Daily Revenue vs COGS" : "Monthly Revenue vs COGS"}
                        </h3>
                        {!showingDaily && (
                            <span className="text-[11px] text-gray-400">Click a bar to see that month day-by-day</span>
                        )}
                        {showingDaily && (
                            <span className="flex items-center gap-3">
                                <span className="text-[11px] text-gray-400">Click a bar to see that day's orders</span>
                                {drilldownMonth && (
                                    <button type="button"
                                        onClick={() => {
                                            const m = completedMonths.find((x) => x.month === drilldownMonth) || { month: drilldownMonth, revenue: 0 };
                                            openMonth(m);
                                        }}
                                        className="flex items-center gap-1 text-xs font-semibold text-teal-700 hover:underline">
                                        <List size={13} /> View all {drilldownMonth} orders
                                    </button>
                                )}
                            </span>
                        )}
                    </div>
                    {!showingDaily && startIsPartial && (
                        <p className="text-[11px] text-gray-400 mb-2">
                            {startMonthKey} excluded — the range only covers a few days of it, which would skew the comparison. The current month is still shown in full, even if in progress.
                        </p>
                    )}
                    <ResponsiveContainer width="100%" height={280}>
                        {showingDaily ? (
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }}
                                    tickFormatter={(d) => d.slice(5)} /* MM-DD, month is implicit at this zoom level */ />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Bar dataKey="revenue" fill="#0d9488" name="Revenue" radius={[2, 2, 0, 0]} cursor="pointer" onClick={handleDayClick} />
                                <Bar dataKey="cogs" fill="#f97316" name="COGS" radius={[2, 2, 0, 0]} cursor="pointer" onClick={handleDayClick} />
                                <Bar dataKey="net_profit" fill="#2563eb" name="Net Profit" radius={[2, 2, 0, 0]} cursor="pointer" onClick={handleDayClick} />
                            </BarChart>
                        ) : (
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Bar dataKey="revenue" fill="#0d9488" name="Revenue" radius={[3, 3, 0, 0]} cursor="pointer" onClick={handleBarClick} />
                                <Bar dataKey="cogs" fill="#f97316" name="COGS" radius={[3, 3, 0, 0]} cursor="pointer" onClick={handleBarClick} />
                                <Bar dataKey="net_profit" fill="#2563eb" name="Net Profit" radius={[3, 3, 0, 0]} cursor="pointer" onClick={handleBarClick} />
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                </div>
            </RefreshableSection>

            <RefreshableSection refreshing={refreshing}>
                <div className="bg-white rounded-2xl shadow-lg p-5 overflow-x-auto max-h-[70vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-gray-800 text-sm">By Month</h3>
                        <span className="text-[11px] text-gray-400">Click a month to see the orders behind it</span>
                    </div>
                    <table className="w-full text-xs">
                        <thead className="sticky top-0 z-10 bg-white">
                            <tr className="text-left text-gray-400 border-b border-gray-100">
                                <SortableTH label="Month" sortKey="month" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Revenue" sortKey="revenue" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="COGS" sortKey="cogs" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Shipping" sortKey="shipping_cost" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Platform Fees" sortKey="platform_fees" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Net Profit" sortKey="net_profit" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Net Margin %" sortKey="net_margin_pct" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Units" sortKey="units_sold" sortConfig={sortConfig} onSort={requestSort} />
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.map((m) => (
                                <tr key={m.month} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
                                    title="Click to see the orders that make up this month" onClick={() => openMonth(m)}>
                                    <td className="py-2 font-semibold whitespace-nowrap">
                                        {m.month}
                                        {(m.missing_cost_count || 0) > 0 && (
                                            <button type="button" onClick={(e) => { e.stopPropagation(); openMonth(m, true); }}
                                                className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full hover:bg-amber-200">
                                                <AlertTriangle size={10} /> {m.missing_cost_count} missing cost
                                            </button>
                                        )}
                                    </td>
                                    <td className="py-2">${m.revenue.toFixed(2)}</td>
                                    <td className="py-2">${m.cogs.toFixed(2)}</td>
                                    <td className="py-2">${m.shipping_cost.toFixed(2)}</td>
                                    <td className="py-2 text-red-600 relative group cursor-help">
                                        ${m.platform_fees.toFixed(2)}
                                        <div className="hidden group-hover:block absolute z-20 bottom-full left-0 mb-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-56 text-left normal-case">
                                            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1.5">Fee Breakdown</p>
                                            <div className="space-y-1 text-xs">
                                                <div className="flex justify-between"><span className="text-gray-500">Referral</span><span className="font-semibold text-gray-700">${m.fee_breakdown_totals.referral_fee.toFixed(2)}</span></div>
                                                <div className="flex justify-between"><span className="text-gray-500">WFS Fee</span><span className="font-semibold text-gray-700">${m.fee_breakdown_totals.wfs_fee.toFixed(2)}</span></div>
                                                <div className="flex justify-between"><span className="text-gray-500">Fulfillment</span><span className="font-semibold text-gray-700">${m.fee_breakdown_totals.fulfillment_cost.toFixed(2)}</span></div>
                                                <div className="flex justify-between"><span className="text-gray-500">Inbound</span><span className="font-semibold text-gray-700">${m.fee_breakdown_totals.inbound_shipping.toFixed(2)}</span></div>
                                                <div className="flex justify-between"><span className="text-gray-500">Sales Tax</span><span className="font-semibold text-gray-700">${m.fee_breakdown_totals.sales_tax.toFixed(2)}</span></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className={`py-2 font-bold ${m.net_profit >= 0 ? "text-green-600" : "text-red-600"}`}>${m.net_profit.toFixed(2)}</td>
                                    <td className="py-2">{m.net_margin_pct}%</td>
                                    <td className="py-2">{m.units_sold}</td>
                                </tr>
                            ))}
                            {pageItems.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-gray-400">No orders in this range</td></tr>}
                        </tbody>
                    </table>
                    <Pagination page={page} totalPages={totalPages} setPage={setPage} totalItems={sorted.length} pageSize={pageSize} />
                </div>
            </RefreshableSection>

            <LineItemsDrawer
                open={!!breakdown}
                onClose={() => setBreakdown(null)}
                onCostSaved={handleForceRefresh}
                {...(breakdown || {})}
            />
        </div>
    );
};

export default PnLReportPage;